import {
  Divider,
  Icon,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Tab,
  Tabs,
  useUniqueId,
} from "@outschool/backpack";
import { MainToolbarPinnedTabs } from "@outschool/gql-backend-generated";
import { faChevronDown, faChevronUp } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  orgTeacherAvailabilityPath,
  pathnameWithoutLocale,
  sellerOrgPath,
  teacherDashboardPath,
  teacherSchedulePath,
  teachTipsUrl,
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { ExternalLink } from "@outschool/ui-components-shared";
import { union } from "lodash";
import React from "react";

import { useBroadcastMessageFlag } from "../../../shared/client/hooks/useBroadcastMessageFlag";
import { EDUCATOR_ESA_HUB_FEATURE_FLAG } from "../../../shared/featureFlags";
import * as Routes from "../../../shared/Routes";
import { EDUCATOR_TOOLBAR_SKELETON } from "../../../shared/TeacherDashboardSkeletonGeometry";
import * as User from "../../../shared/User";
import { UserMode } from "../../../shared/UserMode";
import { useUnreadConversationsBadgeCount } from "../../hooks/useCurrentUserUnreadCounts";
import { useLmsTeacherHomebase } from "../../hooks/useLmsTeacherHomebase";
import { useFeatureFlag, useFeatureFlags } from "../../lib/FeatureFlags";
import { getTeacherSettingsNavigationPolicy } from "../../lib/teacherSettingsAccess";
import IconBadgeWithNumber from "../IconBadgeWithNumber";
import Link from "../Link";
import NewBadge from "./NewBadge";
import PublicFundingNewBadge from "./PublicFundingNewBadge";

const ConversationBadge = () => {
  const unreadConversationsCount = useUnreadConversationsBadgeCount();

  return (
    <IconBadgeWithNumber
      color="red"
      count={unreadConversationsCount}
      dataTestId={"unread-message-count"}
    />
  );
};

const LEGACY_MAX_PINNED_TABS = 4;
const ESA_HUB_MAX_PINNED_TABS = 6;

export type EducatorTab = {
  value: string;
  label: string;
  to: string;
  Badge?: React.FC;
  isExternalLink?: boolean;
};
export type EducatorTabs = {
  [key in MainToolbarPinnedTabs]: EducatorTab;
};

export const useEducatorPinnedTabs = () => {
  const { t } = useTranslation("client\\components\\nav\\EducatorModeToolbar");
  const { currentUser } = useSession();
  const { userFlagsLoaded } = useFeatureFlags();
  const {
    canAccessCredentials,
    isEducatorEsaHubEnabled,
    usesEmbeddedCredentials,
    usesSettingsNavigation,
  } = getTeacherSettingsNavigationPolicy(
    currentUser,
    useFeatureFlag(EDUCATOR_ESA_HUB_FEATURE_FLAG),
  );

  const allAvailableTabsByKey: EducatorTabs = {
    [MainToolbarPinnedTabs.Conversations]: {
      value: MainToolbarPinnedTabs.Conversations,
      label: t("Conversations"),
      to: Routes.conversationsPath(UserMode.Educator),
      Badge: ConversationBadge,
    },
    [MainToolbarPinnedTabs.Classes]: {
      value: MainToolbarPinnedTabs.Classes,
      label: t("Classes"),
      to: Routes.teacherClassesPath(),
    },
    [MainToolbarPinnedTabs.Schedule]: {
      value: MainToolbarPinnedTabs.Schedule,
      label: t("Schedule"),
      to: teacherSchedulePath(),
    },
    [MainToolbarPinnedTabs.Insights]: {
      value: MainToolbarPinnedTabs.Insights,
      label: t("Insights"),
      to: Routes.teacherStatsPath(),
    },
    [MainToolbarPinnedTabs.PublicFunding]: {
      value: MainToolbarPinnedTabs.PublicFunding,
      label: t("Public Funding"),
      to: Routes.publicFundingPath(),
      Badge: PublicFundingNewBadge,
    },
    [MainToolbarPinnedTabs.Profile]: {
      value: MainToolbarPinnedTabs.Profile,
      label: usesSettingsNavigation
        ? t("Teacher settings")
        : t("Teacher account setting"),
      to: Routes.leaderSettingsPath(),
    },
    [MainToolbarPinnedTabs.Sections]: {
      value: MainToolbarPinnedTabs.Sections,
      label: t("Sections"),
      to: Routes.teacherSectionsPath(),
    },
    [MainToolbarPinnedTabs.Availability]: {
      value: MainToolbarPinnedTabs.Availability,
      label: t("Group class availability"),
      to: Routes.teacherAvailabilityPath(),
    },
    [MainToolbarPinnedTabs.Coupons]: {
      value: MainToolbarPinnedTabs.Coupons,
      label: t("Coupons"),
      to: Routes.teacherCouponsPath(),
    },
    [MainToolbarPinnedTabs.Marketing]: {
      value: MainToolbarPinnedTabs.Marketing,
      label: t("Class Settings"),
      to: Routes.teacherMarketingPath(),
    },
    [MainToolbarPinnedTabs.Groups]: {
      value: MainToolbarPinnedTabs.Groups,
      label: t("Groups"),
      to: Routes.teacherClubsPath(),
    },
    [MainToolbarPinnedTabs.Credentials]: {
      value: MainToolbarPinnedTabs.Credentials,
      label: t("Credentials"),
      to: Routes.teacherUploadCredentialsPath(),
    },
  };

  const isMainToolbarPinnedTab = (tab: string): tab is MainToolbarPinnedTabs =>
    tab in allAvailableTabsByKey;
  const isAvailableTab = (tab: MainToolbarPinnedTabs) =>
    !(
      (tab === MainToolbarPinnedTabs.PublicFunding &&
        !usesSettingsNavigation) ||
      (tab === MainToolbarPinnedTabs.Credentials &&
        (!canAccessCredentials || usesEmbeddedCredentials))
    );
  const persistedPinnedTabKeys = (
    currentUser?.leader?.mainToolbarPinnedTabs || []
  ).filter(isMainToolbarPinnedTab);
  const userMainToolbarPinnedTabKeys =
    persistedPinnedTabKeys.filter(isAvailableTab);
  const hiddenPinnedTabKeys = persistedPinnedTabKeys.filter(
    tab => !isAvailableTab(tab),
  );
  const allAvailableTabKeys = Object.keys(allAvailableTabsByKey).filter(
    (tab): tab is MainToolbarPinnedTabs =>
      isMainToolbarPinnedTab(tab) && isAvailableTab(tab),
  );
  const maxPinnedTabs = usesSettingsNavigation
    ? ESA_HUB_MAX_PINNED_TABS
    : LEGACY_MAX_PINNED_TABS;

  // Order the pinned tabs to be in front of the available tabs based on user's preferences
  const orderedTabKeys: MainToolbarPinnedTabs[] = union(
    userMainToolbarPinnedTabKeys,
    allAvailableTabKeys,
    // Make sure tab is valid
  ).filter(isMainToolbarPinnedTab);

  return {
    /**
     * On object with all available tabs by key.
     */
    allAvailableTabsByKey,
    /**
     * An array of all available tabs
     */
    allAvailableTabs: allAvailableTabKeys.map(
      key => allAvailableTabsByKey[key],
    ),
    /**
     * An array of all pinned tab keys set by the user.
     * Invalid tabs are filtered out.
     */
    userMainToolbarPinnedTabKeys,
    /**
     * An array of all tabs, with pinned tabs first.
     * Invalid tabs are filtered out.
     */
    orderedTabs: orderedTabKeys.map(key => allAvailableTabsByKey[key]),
    hiddenPinnedTabKeys,
    isEducatorEsaHubEnabled,
    userFlagsLoaded,
    maxPinnedTabs,
    pinnedTabsPolicyKey: `${usesSettingsNavigation}:${usesEmbeddedCredentials}`,
    usesSettingsNavigation,
    usesEmbeddedCredentials,
  };
};

export const EducatorModeToolbar = () => {
  const { t } = useTranslation("client\\components\\nav\\EducatorModeToolbar");
  const { userFlagsLoaded } = useFeatureFlags();
  const tabs = useEducatorModeToolbarTabs();
  const { value: currentTab } =
    tabs.find(tab =>
      pathnameWithoutLocale(window.location.pathname).startsWith(tab.to),
    ) || {};

  if (!userFlagsLoaded) {
    return (
      <Tabs
        aria-busy={true}
        aria-label={t("Loading educator navigation")}
        sx={{ flex: 1 }}
        value={false}
        variant="scrollable"
      >
        {EDUCATOR_TOOLBAR_SKELETON.tabLabelWidths.map(width => (
          <Tab
            key={width}
            disabled
            label={<Skeleton variant="text" width={width} height={28} />}
          />
        ))}
      </Tabs>
    );
  }

  return (
    <Tabs sx={{ flex: 1 }} value={currentTab || false} variant="scrollable">
      {tabs.map(({ Badge, isExternalLink, ...tab }) => (
        <Tab
          key={tab.value}
          {...tab}
          url={isExternalLink ? tab.to : undefined}
          component={isExternalLink ? ExternalLink : Link}
          icon={Badge ? <Badge /> : undefined}
          // eslint-disable-next-line i18next/no-literal-string
          iconPosition="end"
        />
      ))}
      <ToolbarOverflowMenu />
    </Tabs>
  );
};

const useEducatorModeToolbarTabs = (): EducatorTab[] => {
  const { t } = useTranslation("client\\components\\nav\\EducatorModeToolbar");
  const { currentUser } = useSession();

  const {
    allAvailableTabsByKey,
    orderedTabs,
    isEducatorEsaHubEnabled,
    maxPinnedTabs,
    usesSettingsNavigation,
  } = useEducatorPinnedTabs();
  const learnersTabs: EducatorTab[] = useLmsTeacherHomebase()
    ? [
        {
          value: "learners",
          label: t("Learners"),
          to: Routes.teacherLearnersPath(),
          Badge: NewBadge,
        },
      ]
    : [];

  const isOrgTeacher = currentUser?.sellerOrg?.currentUserIsTeacher;
  if (isOrgTeacher) {
    return [
      {
        value: "schedule",
        label: t("Schedule"),
        to: teacherSchedulePath(),
      },
      {
        value: "sections",
        label: t("Sections"),
        to: Routes.teacherSectionsPath(),
      },
      {
        value: "availability",
        label: t("1-on-1 Availability"),
        to: orgTeacherAvailabilityPath(),
      },
      {
        value: "groups",
        label: t("Groups"),
        to: Routes.teacherClubsPath(),
      },
      {
        value: "conversations",
        label: t("Conversations"),
        to: Routes.conversationsPath(UserMode.Educator),
        Badge: ConversationBadge,
      },
      ...learnersTabs,
      {
        value: "educator-library",
        label: t("Educator Library"),
        to: teachTipsUrl(),
        isExternalLink: true,
      },
      {
        value: "credentials",
        label: t("Credentials"),
        to: Routes.teacherUploadCredentialsPath(),
      },
    ];
  }

  const isOrgApplicant = !!currentUser?.latestSellerOrgApplication;
  const isTeacherApplicant = User.hasTeacherApplication(currentUser);

  const userTabs: EducatorTab[] = orderedTabs.slice(0, maxPinnedTabs);
  const conversationsIndex = userTabs.findIndex(
    tab => tab.value === MainToolbarPinnedTabs.Conversations,
  );
  userTabs.splice(
    conversationsIndex === -1 ? userTabs.length : conversationsIndex + 1,
    0,
    ...learnersTabs,
  );

  const tabs: EducatorTab[] = [
    { value: "home", label: t("Home"), to: teacherDashboardPath() },
    ...(isTeacherApplicant
      ? [{ value: "apply", label: t("Apply"), to: Routes.teacherApplyPath() }]
      : []),
    ...userTabs,
    ...(isEducatorEsaHubEnabled && !usesSettingsNavigation
      ? [allAvailableTabsByKey[MainToolbarPinnedTabs.PublicFunding]]
      : []),
    ...(isOrgApplicant
      ? [{ value: "manage", label: t("Manage"), to: sellerOrgPath() }]
      : []),
  ];

  return tabs;
};

const useToolbarOverflowMenuItems = () => {
  const { currentUser } = useSession();
  const { maxPinnedTabs, orderedTabs } = useEducatorPinnedTabs();
  const isOrgTeacher = currentUser?.sellerOrg?.currentUserIsTeacher;

  if (isOrgTeacher) {
    return [];
  }

  // Overflow menu includes all pinned tabs that are not shown in the main toolbar
  return orderedTabs.slice(maxPinnedTabs);
};

const ToolbarOverflowMenu = () => {
  const { currentUser } = useSession();
  const canUseBroadcastMessaging = useBroadcastMessageFlag(currentUser);
  const isOrgTeacher = currentUser?.sellerOrg?.currentUserIsTeacher;
  const { t } = useTranslation("client\\components\\nav\\EducatorModeToolbar");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);

  const buttonId = useUniqueId("ToolbarOverflowMenu-button");
  const menuId = useUniqueId("ToolbarOverflowMenu-menu");

  const menuItems = useToolbarOverflowMenuItems();

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (isOrgTeacher) {
    return null;
  }

  return (
    <>
      <Tab
        label={t("Menu")}
        icon={<Icon icon={isOpen ? faChevronUp : faChevronDown} />}
        // eslint-disable-next-line i18next/no-literal-string
        iconPosition="end"
        onClick={handleButtonClick}
        id={buttonId}
        sx={{
          flexDirection: "row",
          color: "text.primary",
          // gap matches space between text and icon in Button
          gap: 8,
        }}
      />
      <Menu
        id={menuId}
        aria-labelledby={buttonId}
        open={isOpen}
        onClose={handleMenuClose}
        anchorEl={anchorEl}
        /* eslint-disable i18next/no-literal-string */
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        /* eslint-enable i18next/no-literal-string */
      >
        {menuItems.map(({ value, to, label, Badge }) => (
          <MenuItem key={value} component={Link} to={to}>
            <ListItemText>{label}</ListItemText>
            {Badge && (
              <ListItemIcon>
                <Badge />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
        {menuItems.length > 0 && <EducatorMenuDivider />}
        <MenuItem component={Link} to={Routes.activityCreatePath()}>
          <ListItemText>{t("Create new class")}</ListItemText>
        </MenuItem>
        <MenuItem component={ExternalLink} url={teachTipsUrl()}>
          <ListItemText>{t("Educator Library")}</ListItemText>
        </MenuItem>
        {canUseBroadcastMessaging && (
          <MenuItem component={ExternalLink} url={"/teach/broadcast"}>
            <ListItemText>{t("Broadcast Message")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const EducatorMenuDivider = () => (
  <Divider
    sx={{
      marginY: "0.8rem !important",
    }}
  />
);
